import { AccordionForm, Button, Loader, Text } from "@southfields-digital/mxmz-react-ui";
import { isEqual } from "lodash";
import { type ComponentProps, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CanvasForm from "src/components/CanvasForm";
import CanvasGraphicsForm from "src/components/CanvasGraphicsForm";
import RundownMatchControlForm from "src/components/RundownMatchControl/RundownMatchControl";
import RundownTemplateForm from "src/components/RundownTemplateForm";
import { useNavigationBlocker } from "src/hooks";
import useAuth from "src/hooks/useAuth";
import { BaseLayoutContext, type BaseLayoutContextType } from "src/layouts/BaseLayout";
import type { RundownTemplateSuccessState } from "src/redux/reducers/rundownTemplate";
import type { SportCategory } from "src/types/matchControl/sport";
import { sortBundledGraphics } from "src/utils/sortBundledGraphics";

import styles from "./RundownTemplateForm.module.scss";

type Section = ComponentProps<typeof AccordionForm>["sections"][number];

type RundownTemplateFormPageProps = {
	canvasesData: Canvas[];
	canvasBackgroundsData: CanvasBackground[];
	error: string | null;
	getCanvases: () => void;
	getCanvasBackgrounds: () => void;
	getGraphicPackages: (deprecated: boolean) => void;
	getGraphicsByGraphicPackageId: (graphicPackageId: string) => void;
	getRundownTemplate: () => void;
	getMatchControlTypes: () => void;
	clearRundownTemplateSuccess: () => void;
	graphicPackagesData: GraphicPackage[];
	graphicsData: Graphic[];
	matchControlTypesData: SportCategory[];
	handleSubmit: (payload: Partial<RundownTemplate>) => void;
	loadingRundownTemplateData?: boolean;
	rundownTemplateData: Partial<RundownTemplate>;
	rundownTemplateId?: string;
	submitting: boolean;
	createSuccess: RundownTemplateSuccessState;
	editSuccess: RundownTemplateSuccessState;
};

const SECTION_IDS = {
	TEMPLATE: "template",
	GRAPHICS: "graphics",
	BUNDLE: "bundle",
	MATCH_CONTROL: "matchControl",
	CANVAS: "canvas",
} as const;

export default function RundownTemplateFormPage({
	canvasesData,
	canvasBackgroundsData,
	error,
	getCanvases,
	getCanvasBackgrounds,
	getGraphicPackages,
	getGraphicsByGraphicPackageId,
	getRundownTemplate,
	getMatchControlTypes,
	clearRundownTemplateSuccess,
	graphicPackagesData,
	graphicsData,
	matchControlTypesData,
	handleSubmit,
	loadingRundownTemplateData,
	rundownTemplateData,
	rundownTemplateId,
	submitting,
	createSuccess,
	editSuccess,
}: RundownTemplateFormPageProps) {
	const { setWithContainer } = useContext<BaseLayoutContextType | null>(
		BaseLayoutContext,
	) as BaseLayoutContextType;
	const navigate = useNavigate();

	const auth = useAuth();
	const isBetaUser = auth.isBetaUser();

	const createOrUpdate = rundownTemplateId ? "Update" : "Create";
	const initialRundownTemplateData = useMemo(() => rundownTemplateData, [rundownTemplateData]);
	const [state, setState] = useState<Partial<RundownTemplate>>(rundownTemplateData);

	const [selectedSectionId, setSelectedSectionId] = useState<string>(SECTION_IDS.TEMPLATE);
	const [visitedSections, setVisitedSections] = useState<string[]>(
		createOrUpdate === "Update"
			? Object.values(SECTION_IDS).filter(() => isBetaUser)
			: [SECTION_IDS.TEMPLATE],
	);

	// Determine if there are unsaved changes by comparing current state with initial data
	const isDirty = useMemo(
		() => !isEqual(state, initialRundownTemplateData),
		[state, initialRundownTemplateData],
	);

	// Navigate after successful submission
	useEffect(() => {
		if (submitting) return;

		const success = createOrUpdate === "Create" ? createSuccess : editSuccess;
		if (success?.completed && success.rundownTemplateId) {
			clearRundownTemplateSuccess();
			navigate("/settings/templates");
		}
	}, [
		createSuccess,
		editSuccess,
		submitting,
		createOrUpdate,
		navigate,
		clearRundownTemplateSuccess,
	]);

	// Block navigation when there are unsaved changes (but not after successful submission)
	useNavigationBlocker({
		shouldBlock: isDirty && !submitting && !editSuccess?.completed && !createSuccess?.completed,
		message: "You have unsaved changes. Are you sure you want to leave?",
	});

	const updateState = (partialState: Partial<RundownTemplate>) => {
		setState((prevState: Partial<RundownTemplate>) => {
			const hasRelevantChange = Object.keys(partialState).some(
				(key) =>
					!Object.is(
						prevState[key as keyof RundownTemplate],
						partialState[key as keyof RundownTemplate],
					),
			);

			if (!hasRelevantChange) {
				// No new changes
				return prevState;
			}

			// New changes
			return {
				...prevState,
				...partialState,
			};
		});
	};

	const handleChangeSection = (id: string, sections?: Section[]) => {
		// Close if we click on the open section
		if (id === selectedSectionId) {
			setSelectedSectionId("");
			return;
		}

		// Check if section we want to navigate to is locked if we provided all the sections
		if (sections) {
			const section = sections.find((section) => section.id === id);
			if (section?.locked) return;
		}

		setVisitedSections((prevVisitedSections) => {
			if (!prevVisitedSections.includes(id)) {
				return [...prevVisitedSections, id];
			}

			return prevVisitedSections;
		});

		setSelectedSectionId(id);
	};

	const handleClickNext = (sections: Section[]) => {
		const currentSectionIndex = sections.findIndex((section) => section.id === selectedSectionId);
		const nextSection = sections[currentSectionIndex + 1];

		if (!nextSection) return;
		handleChangeSection(nextSection.id, sections);
	};

	useEffect(() => {
		setWithContainer(true);
		clearRundownTemplateSuccess(); // Clear any stale success state from previous operations
		getGraphicPackages(!!rundownTemplateId);
		getMatchControlTypes();
		getRundownTemplate();

		return () => {
			setWithContainer(false);
		};
	}, []);

	useEffect(() => {
		if (rundownTemplateId) {
			setState(rundownTemplateData);
		}
	}, [rundownTemplateId, rundownTemplateData]);

	useEffect(() => {
		if (state?.graphicPackageId) {
			getCanvases();
			getCanvasBackgrounds();
			getGraphicsByGraphicPackageId(state?.graphicPackageId);
			getMatchControlTypes();
		}
	}, [state?.graphicPackageId]);

	if (loadingRundownTemplateData) return <Loader centeredFullscreen />;
	if (error) return <Text as="h1">Error: {error}</Text>;
	if (rundownTemplateId && !rundownTemplateData)
		return <Text as="h1">Could not get rundown template data</Text>;

	const accordionFormTitle = `${createOrUpdate} template`;

	const templateSectionCompleted = Boolean(
		state.name && state.graphicPackageId && visitedSections.includes(SECTION_IDS.TEMPLATE),
	);
	const graphicsSectionCompleted = Boolean(
		state.canvases && templateSectionCompleted && visitedSections.includes(SECTION_IDS.GRAPHICS),
	);
	const bundleSectionCompleted = Boolean(
		state.canvases && graphicsSectionCompleted && visitedSections.includes(SECTION_IDS.BUNDLE),
	);
	const matchControlSectionCompleted = Boolean(
		bundleSectionCompleted && visitedSections.includes(SECTION_IDS.MATCH_CONTROL),
	);
	const canvasSectionCompleted = Boolean(
		state.canvases && matchControlSectionCompleted && visitedSections.includes(SECTION_IDS.CANVAS),
	);

	const templateSectionLocked = false;
	const graphicsSectionLocked = !templateSectionCompleted;
	const bundleSectionLocked = !graphicsSectionCompleted;
	const matchControlSectionLocked = !bundleSectionCompleted;
	const canvasSectionLocked = !matchControlSectionCompleted;

	const canSubmit = state.name && state.graphicPackageId;

	const sections: Section[] = [
		{
			id: SECTION_IDS.TEMPLATE,
			type: "disclosure",
			title: "Template",
			completed: templateSectionCompleted,
			locked: templateSectionLocked,
			form: (
				<RundownTemplateForm
					graphicsPackages={graphicPackagesData.map(({ id, name, version, deprecated }) => ({
						label: `${name} (${version})`,
						value: id,
						disabled: deprecated,
					}))}
					state={state}
					stateUpdated={(update) => updateState(update)}
				/>
			),
			info: (
				<>
					<Text as="h2">Template details</Text>

					<Text as="p">
						Create a template for your desired setup. When creating a rundown you can select the
						rundowns you have saved as a template.
					</Text>
				</>
			),
		},
		{
			id: SECTION_IDS.GRAPHICS,
			type: "disclosure",
			title: "Graphics",
			completed: graphicsSectionCompleted,
			locked: graphicsSectionLocked,
			form: (
				<CanvasGraphicsForm
					title="Graphic"
					canvases={canvasesData}
					graphics={graphicsData.filter((g) => g.types.includes("general") && !g.parentId)}
					state={state.canvases || []}
					stateUpdated={(update) => updateState({ canvases: update })}
				/>
			),
			info: (
				<>
					<Text as="h2">Configure your canvas</Text>
					<Text as="p">
						Select your graphics package and assign them to the appropriate canvas.
					</Text>
					<Text as="p">
						Currently your workspace supports {2} canvasses. When creating a rundown you can assign
						multiple canvasses to an output.
					</Text>
				</>
			),
		},
		{
			id: SECTION_IDS.BUNDLE,
			type: "disclosure",
			title: "Bundles",
			completed: bundleSectionCompleted,
			locked: bundleSectionLocked,
			form: (
				<CanvasGraphicsForm
					title="Bundle"
					showToggleAll={false}
					canvases={canvasesData}
					state={state.canvases || []}
					stateUpdated={(canvases) => updateState({ canvases })}
					graphics={sortBundledGraphics(
						graphicsData.filter(
							(g) => g.types.includes("bundle") || (g.parentId && g.types.includes("general")),
						),
					)}
				/>
			),
			info: <></>,
		},
		{
			id: SECTION_IDS.MATCH_CONTROL,
			type: "disclosure",
			title: "Match Control",
			completed: matchControlSectionCompleted,
			locked: matchControlSectionLocked,
			form: (
				<div>
					<RundownMatchControlForm
						categories={matchControlTypesData}
						handleChangeCategory={(sport) => {
							if (sport === "") {
								updateState({ matchControlType: undefined });
								return;
							}
							updateState({ matchControlType: sport });
						}}
						matchControlState={state.matchControlType ? { sport: state.matchControlType } : null}
						matchControlStateUpdated={() => {}}
					/>
					{state.matchControlType && (
						<CanvasGraphicsForm
							title="Graphic"
							canvases={canvasesData}
							state={state.canvases || []}
							stateUpdated={(canvases) => updateState({ canvases })}
							graphics={graphicsData.filter((g) => g.types.includes("matchControl"))}
						/>
					)}
				</div>
			),
			info: <></>,
		},
		{
			id: SECTION_IDS.CANVAS,
			type: "disclosure",
			title: "Canvas",
			completed: canvasSectionCompleted,
			locked: canvasSectionLocked,
			form: (
				<CanvasForm
					state={state.canvases || []}
					canvases={canvasesData}
					canvasBackgrounds={canvasBackgroundsData}
					stateUpdated={(update) => updateState({ canvases: update })}
				/>
			),
			info: (
				<>
					<Text as="h2">Configure your canvas</Text>
					<Text as="p">
						Select a background for the canvas. Graphics will be served over the selected
						background.
					</Text>
				</>
			),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<AccordionForm
				title={accordionFormTitle}
				handleChangeSection={(id) => handleChangeSection(id, sections)}
				handleClickNext={() => handleClickNext(sections)}
				openId={selectedSectionId}
				sections={sections}
			/>
			<div className="grid grid-cols-12 gap-x-6">
				<Button
					className={styles.RundownTemplateFormSubmitButton}
					disabled={submitting || !canSubmit}
					onClick={() => {
						handleSubmit(state);
					}}
				>
					{submitting && <Loader className={styles.Spinner} />}
					{accordionFormTitle}
				</Button>
			</div>
		</div>
	);
}
